import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Plus, Edit2, Trash2, X, AlertTriangle, ShieldCheck, MessageCircle, Send, CheckCircle2, Filter, RotateCcw, FileText, BookOpen, Download } from "lucide-react";
import { toast } from "sonner";

const QUOTA_OPTIONS = ["A", "B", "C", "D"];

async function downloadDisclaimerPdf(filename) {
    try {
        const res = await apiPrivate.get("/market/disclaimer-pdf", { responseType: "blob" });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "disclaimer-mercato-secondario.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
        toast.error(e?.response?.status === 404 ? "Il PDF non è stato ancora caricato dall'amministrazione." : "Errore nel download del PDF");
    }
}

export default function Mercato() {
    const { user } = useAuth();
    const [disclaimer, setDisclaimer] = useState(null);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contactTarget, setContactTarget] = useState(null);
    const [showRereadModal, setShowRereadModal] = useState(false);
    // Filters (client-side, all listings already loaded)
    const [filterQuotas, setFilterQuotas] = useState([]);
    const [priceMin, setPriceMin] = useState("");
    const [priceMax, setPriceMax] = useState("");
    const [sortBy, setSortBy] = useState("newest");

    const refresh = async () => {
        setLoading(true);
        const d = await apiPrivate.get("/market/disclaimer");
        setDisclaimer(d.data);
        if (d.data.accepted) {
            const all = await apiPrivate.get("/market/listings");
            setListings(all.data || []);
        }
        setLoading(false);
    };
    useEffect(() => { refresh(); }, []);

    const accept = async () => {
        await apiPrivate.post("/market/disclaimer/accept");
        refresh();
    };

    // Filtered + sorted listings for the public board (excluding mine)
    const otherListings = useMemo(() => listings.filter(l => !l.is_mine), [listings]);
    const visibleListings = useMemo(() => {
        let arr = [...otherListings];
        if (filterQuotas.length > 0) {
            arr = arr.filter(l => filterQuotas.includes(l.quota_category));
        }
        const min = priceMin === "" ? null : parseFloat(priceMin);
        const max = priceMax === "" ? null : parseFloat(priceMax);
        // Filtro sul numero di quote. Se un annuncio non ha `numero_quote` calcolabile,
        // viene incluso solo quando non ci sono filtri min/max attivi.
        if (min !== null && !isNaN(min)) arr = arr.filter(l => l.numero_quote != null && l.numero_quote >= min);
        if (max !== null && !isNaN(max)) arr = arr.filter(l => l.numero_quote != null && l.numero_quote <= max);
        switch (sortBy) {
            case "qty_asc": arr.sort((a, b) => (a.numero_quote ?? Infinity) - (b.numero_quote ?? Infinity)); break;
            case "qty_desc": arr.sort((a, b) => (b.numero_quote ?? -Infinity) - (a.numero_quote ?? -Infinity)); break;
            default: arr.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        }
        return arr;
    }, [otherListings, filterQuotas, priceMin, priceMax, sortBy]);

    const filtersActive = filterQuotas.length > 0 || priceMin !== "" || priceMax !== "" || sortBy !== "newest";
    const resetFilters = () => {
        setFilterQuotas([]); setPriceMin(""); setPriceMax(""); setSortBy("newest");
    };
    const toggleQuota = (q) => setFilterQuotas(qs => qs.includes(q) ? qs.filter(x => x !== q) : [...qs, q]);

    if (loading) {
        return <AppLayout><div className="p-10 text-zinc-500" data-testid="mercato-loading"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Caricamento mercato…</div></AppLayout>;
    }

    if (!disclaimer?.accepted) {
        return (
            <AppLayout>
                <DisclaimerGate disclaimer={disclaimer} onAccept={accept} />
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="max-w-[1400px] mx-auto" data-testid="mercato-page">
                {/* Header */}
                <div>
                    <span className="label-tag text-magenta">Bacheca annunci soci</span>
                    <h1 className="font-display text-5xl mt-3 tracking-tighter">
                        Bacheca <span className="font-serif-italic text-gradient-tropical">quote</span>.
                    </h1>
                    <p className="text-zinc-400 mt-3 max-w-2xl">
                        Vetrina delle quote in vendita tra i soci. Annunci anonimi: categoria e quantità, senza nome del proponente. Per mettere in vendita una tua quota vai in <a href="/app/investimento" className="text-acid hover:underline">Il mio investimento</a>.
                    </p>
                </div>

                <div className="bg-vermillion/10 border-l-4 border-vermillion rounded-r-2xl p-4 mt-6 flex flex-col sm:flex-row items-start gap-3" data-testid="disclaimer-banner">
                    <AlertTriangle className="h-5 w-5 text-vermillion shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm text-zinc-300">
                            <strong className="text-white">Bacheca informativa.</strong> La società non valida prezzi né garantisce le trattative. Hai accettato il disclaimer <code className="text-bayside font-mono">{disclaimer.version}</code> in data {new Date(disclaimer.accepted_at).toLocaleDateString("it-IT")}.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <button
                                onClick={() => setShowRereadModal(true)}
                                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-bayside hover:text-white transition"
                                data-testid="reread-disclaimer"
                            >
                                <BookOpen className="h-3.5 w-3.5" /> Rileggi il disclaimer
                            </button>
                            {disclaimer.pdf_available && (
                                <button
                                    onClick={() => downloadDisclaimerPdf(disclaimer.pdf_filename)}
                                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-magenta hover:text-white transition"
                                    data-testid="download-disclaimer-pdf"
                                >
                                    <Download className="h-3.5 w-3.5" /> Scarica PDF
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* All listings */}
                <div className="mt-10" data-testid="public-board">
                    <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
                        <h2 className="font-display text-2xl tracking-tight">
                            Annunci attivi <span className="text-zinc-500 text-base ml-2">
                                {visibleListings.length === otherListings.length
                                    ? `(${otherListings.length})`
                                    : `(${visibleListings.length} di ${otherListings.length})`}
                            </span>
                        </h2>
                        {filtersActive && (
                            <button onClick={resetFilters} data-testid="mercato-reset-filters" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-vermillion transition">
                                <RotateCcw className="h-3.5 w-3.5" /> Reset filtri
                            </button>
                        )}
                    </div>

                    {/* Filter bar */}
                    <div className="bento p-5 mb-5" data-testid="mercato-filters">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                            {/* Quota pills */}
                            <div className="lg:col-span-5">
                                <div className="label-tag mb-2 flex items-center gap-1.5"><Filter className="h-3 w-3" /> Categoria quota</div>
                                <div className="flex flex-wrap gap-2">
                                    {QUOTA_OPTIONS.map(q => {
                                        const active = filterQuotas.includes(q);
                                        const count = otherListings.filter(l => l.quota_category === q).length;
                                        return (
                                            <button
                                                key={q}
                                                onClick={() => toggleQuota(q)}
                                                disabled={count === 0}
                                                data-testid={`mercato-filter-quota-${q}`}
                                                className={`text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border transition ${
                                                    active
                                                        ? "border-bayside bg-bayside/15 text-white"
                                                        : count === 0
                                                            ? "border-white/5 text-zinc-700 cursor-not-allowed"
                                                            : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                                                }`}
                                            >
                                                Quota {q} <span className={`ml-1 ${active ? "text-bayside" : "text-zinc-600"}`}>({count})</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Numero quote range */}
                            <div className="lg:col-span-4">
                                <div className="label-tag mb-2">Numero quote</div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={priceMin}
                                        onChange={(e) => setPriceMin(e.target.value)}
                                        placeholder="Min"
                                        data-testid="mercato-filter-quote-min"
                                        className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl"
                                    />
                                    <span className="text-zinc-600 text-xs">—</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={priceMax}
                                        onChange={(e) => setPriceMax(e.target.value)}
                                        placeholder="Max"
                                        data-testid="mercato-filter-quote-max"
                                        className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl"
                                    />
                                </div>
                            </div>

                            {/* Sort */}
                            <div className="lg:col-span-3">
                                <div className="label-tag mb-2">Ordina per</div>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    data-testid="mercato-filter-sort"
                                    className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl"
                                >
                                    <option value="newest">Più recenti</option>
                                    <option value="qty_asc">Quote — dal minore</option>
                                    <option value="qty_desc">Quote — dal maggiore</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {visibleListings.length === 0 ? (
                        <div className="bento p-10 text-center text-zinc-500" data-testid="mercato-empty">
                            {otherListings.length === 0
                                ? "Nessun annuncio attivo al momento. Torna a controllare più avanti."
                                : "Nessun annuncio corrisponde ai filtri selezionati."}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
                            {visibleListings.map(l => (
                                <div key={l.id} className="bento p-5 flex flex-col" data-testid={`listing-${l.id}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <span className="text-[10px] uppercase tracking-widest text-bayside font-grotesk">Quota {l.quota_category}</span>
                                        <span className="text-[10px] text-zinc-500 font-mono">{l.anonymous_label.split("·")[1]?.trim()}</span>
                                    </div>
                                    {l.numero_quote != null ? (
                                        <>
                                            <div className="font-display text-3xl text-magenta" data-testid={`listing-quote-count-${l.id}`}>
                                                {Number(l.numero_quote).toLocaleString("it-IT", { maximumFractionDigits: 2 })}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mt-1">Quote in vendita</div>
                                        </>
                                    ) : (
                                        <div className="text-xs text-zinc-500 italic font-grotesk">Numero quote non disponibile</div>
                                    )}
                                    {l.note && <p className="text-zinc-400 text-xs mt-3 line-clamp-3">{l.note}</p>}
                                    <button onClick={() => setContactTarget(l)} className="btn-rebel mt-5 justify-center text-xs" data-testid={`contact-${l.id}`}>
                                        <MessageCircle className="h-3.5 w-3.5" /> Contatta
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {contactTarget && (
                    <ContactForm
                        listing={contactTarget}
                        onClose={() => setContactTarget(null)}
                    />
                )}

                {showRereadModal && (
                    <RereadDisclaimerModal
                        disclaimer={disclaimer}
                        onClose={() => setShowRereadModal(false)}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function RereadDisclaimerModal({ disclaimer, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="reread-disclaimer-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-2xl w-full p-7 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4 gap-3">
                    <div>
                        <span className="label-tag">— Disclaimer</span>
                        <h2 className="font-display text-2xl tracking-tight mt-2">Condizioni d'uso della Bacheca annunci soci.</h2>
                        <p className="text-xs text-zinc-500 mt-1 font-grotesk">
                            Versione <code className="text-bayside font-mono">{disclaimer.version}</code> · Accettata il {new Date(disclaimer.accepted_at).toLocaleString("it-IT")}
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="reread-close"><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
                </div>
                <div className="flex-1 overflow-y-auto bento p-5 text-sm text-zinc-300 whitespace-pre-line leading-relaxed" data-testid="reread-text">
                    {disclaimer.text}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                    <p className="text-xs text-zinc-500 max-w-md">
                        Se le condizioni verranno modificate in modo sostanziale, ti verrà chiesto di accettare esplicitamente la nuova versione.
                    </p>
                    <div className="flex gap-2">
                        {disclaimer.pdf_available && (
                            <button
                                onClick={() => downloadDisclaimerPdf(disclaimer.pdf_filename)}
                                className="btn-ghost text-xs"
                                data-testid="reread-download-pdf"
                            >
                                <Download className="h-3.5 w-3.5" /> Scarica PDF
                            </button>
                        )}
                        <button onClick={onClose} className="btn-rebel text-xs" data-testid="reread-ok">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Ho capito
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DisclaimerGate({ disclaimer, onAccept }) {
    const [accepted, setAccepted] = useState(false);
    const [pending, setPending] = useState(false);
    return (
        <div className="max-w-2xl mx-auto py-12" data-testid="disclaimer-gate">
            <span className="label-tag text-magenta">Bacheca annunci soci</span>
            <h1 className="font-display text-4xl mt-3 tracking-tighter">Prima di entrare, <span className="font-serif-italic text-gradient-tropical">una nota</span>.</h1>
            <p className="text-zinc-400 mt-3 text-sm">
                Per accedere alla Bacheca annunci soci devi prendere visione e accettare esplicitamente il disclaimer di responsabilità (versione <code className="text-bayside font-mono">{disclaimer?.version}</code>).
            </p>
            <div className="bento p-6 mt-8 text-sm text-zinc-300 whitespace-pre-line leading-relaxed max-h-[420px] overflow-y-auto" data-testid="disclaimer-text">
                {disclaimer?.text || "Caricamento…"}
            </div>
            {disclaimer?.pdf_available && (
                <div className="mt-4 flex items-center gap-3 bento p-4">
                    <FileText className="h-5 w-5 text-magenta shrink-0" />
                    <div className="flex-1 text-sm">
                        <div className="text-white">Versione firmata dall'amministrazione</div>
                        <div className="text-xs text-zinc-500 font-grotesk">{disclaimer.pdf_filename}</div>
                    </div>
                    <button
                        onClick={() => downloadDisclaimerPdf(disclaimer.pdf_filename)}
                        className="btn-ghost text-xs"
                        data-testid="disclaimer-gate-download-pdf"
                    >
                        <Download className="h-3.5 w-3.5" /> Scarica PDF
                    </button>
                </div>
            )}
            <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="disclaimer-accept-check">
                <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-1 accent-bayside"
                />
                <span className="text-sm text-zinc-300">
                    Ho letto e accetto le condizioni d'uso della Bacheca annunci soci (versione <code className="text-bayside">{disclaimer?.version}</code>).
                </span>
            </label>
            <button
                onClick={async () => { setPending(true); await onAccept(); setPending(false); }}
                disabled={!accepted || pending}
                className="btn-rebel mt-6"
                data-testid="disclaimer-accept-btn"
            >
                {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Accetto e accedo alla bacheca
            </button>
        </div>
    );
}

function ListingForm({ initial, userQuotas, onClose, onSaved }) {
    const [form, setForm] = useState({
        quota_category: initial?.quota_category || userQuotas[0] || "A",
        quantity: initial?.quantity ?? "",
        valore_richiesto: initial?.valore_richiesto ?? "",
        note: initial?.note || "",
    });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                quantity: parseFloat(form.quantity),
                valore_richiesto: parseFloat(form.valore_richiesto),
            };
            if (initial) {
                await apiPrivate.patch(`/market/listings/${initial.id}`, payload);
                toast.success("Inserzione aggiornata");
            } else {
                await apiPrivate.post("/market/listings", payload);
                toast.success("Inserzione pubblicata");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
        setSaving(false);
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="listing-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-5">
                    <div>
                        <span className="label-tag">— {initial ? "Modifica" : "Nuova"} inserzione</span>
                        <h2 className="font-display text-2xl tracking-tight mt-2">Pubblica un annuncio.</h2>
                    </div>
                    <button onClick={onClose} data-testid="listing-modal-close"><X className="h-5 w-5 text-zinc-500" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <div className="label-tag mb-2">Categoria quota *</div>
                        <select value={form.quota_category} onChange={(e) => setForm(s => ({ ...s, quota_category: e.target.value }))} data-testid="listing-quota" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-bayside rounded-xl">
                            {userQuotas.length === 0 ? <option value="A">A</option> : userQuotas.map(q => <option key={q} value={q}>Quota {q}</option>)}
                        </select>
                        <p className="text-[10px] text-zinc-500 mt-1 font-grotesk">Puoi inserire solo quote di cui sei intestatario.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-tag mb-2">Quantità (%) *</div>
                            <input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm(s => ({ ...s, quantity: e.target.value }))} data-testid="listing-quantity" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-bayside rounded-xl" />
                        </div>
                        <div>
                            <div className="label-tag mb-2">Valore richiesto (€) *</div>
                            <input type="number" step="100" value={form.valore_richiesto} onChange={(e) => setForm(s => ({ ...s, valore_richiesto: e.target.value }))} data-testid="listing-price" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-bayside rounded-xl" />
                        </div>
                    </div>
                    <div>
                        <div className="label-tag mb-2">Note</div>
                        <textarea rows={4} value={form.note} onChange={(e) => setForm(s => ({ ...s, note: e.target.value }))} data-testid="listing-note" placeholder="Es. Trattabile, cessione parziale, ecc." className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk text-sm focus:outline-none focus:border-bayside resize-none rounded-xl" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} className="btn-ghost" data-testid="listing-cancel">Annulla</button>
                    <button onClick={submit} disabled={saving || !form.quantity || !form.valore_richiesto} className="btn-rebel" data-testid="listing-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (initial ? "Aggiorna" : "Pubblica")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ContactForm({ listing, onClose }) {
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const send = async () => {
        setSending(true);
        try {
            await apiPrivate.post(`/market/listings/${listing.id}/contact`, { message: msg });
            setSent(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
            setSending(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="contact-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <span className="label-tag">— Contatta</span>
                        <h2 className="font-display text-2xl tracking-tight mt-2">{listing.anonymous_label}</h2>
                    </div>
                    <button onClick={onClose} data-testid="contact-close"><X className="h-5 w-5 text-zinc-500" /></button>
                </div>
                {sent ? (
                    <div className="text-center py-6" data-testid="contact-sent">
                        <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                        <p className="text-white font-display text-xl">Messaggio inviato.</p>
                        <p className="text-zinc-400 text-sm mt-2">L'inserzionista riceverà la tua email e potrà risponderti direttamente.</p>
                        <button onClick={onClose} className="btn-ghost mt-5">Chiudi</button>
                    </div>
                ) : (
                    <>
                        <p className="text-zinc-400 text-sm mb-3">
                            Scrivi un messaggio. L'inserzionista riceverà una email contenente il tuo messaggio e il tuo indirizzo per risponderti.
                        </p>
                        <textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Ciao, sono interessato/a alla tua inserzione…" data-testid="contact-msg" className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk text-sm focus:outline-none focus:border-bayside resize-none rounded-xl" />
                        <div className="flex justify-end gap-3 mt-4">
                            <button onClick={onClose} className="btn-ghost" data-testid="contact-cancel">Annulla</button>
                            <button onClick={send} disabled={sending || msg.length < 10} className="btn-rebel" data-testid="contact-send">
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Invia</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
